import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, CheckCircle2, Pencil, Gauge } from "lucide-react";
import { MapPicker } from "@/components/MapPicker";
import { RideTimeline } from "@/components/RideTimeline";
import { StatusBadge } from "@/components/StatusBadge";
import { PlaceSearch } from "@/components/customer/PlaceSearch";
import { api, errMsg, fmtDate, kina } from "@/lib/api";
import { kmBetween, useGps } from "@/lib/useDriverGps";
import { useRoute } from "@/lib/useRoute";
import { StarPicker, Stars } from "@/components/Stars";
import { DriverAvatar } from "@/components/DriverPhoto";

export const RateRide = ({ ride, onRated }) => {
  if (ride.rating) {
    return <p className="flex items-center gap-2 text-sm text-slate-600" data-testid={`ride-rating-${ride.id}`}><Stars value={ride.rating} /> Thanks for rating!</p>;
  }
  const submit = async (stars, comment) => {
    try {
      await api.post(`/bookings/${ride.id}/rate`, { stars, comment });
      toast.success("Thanks for rating your driver!");
      onRated(stars);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return <StarPicker onSubmit={submit} testPrefix={`rate-${ride.id}`} />;
};

const TaxiTracker = ({ ride, route }) => {
  const loc = ride.driver_location;
  if (!loc) {
    return <p data-testid="taxi-location-unavailable" className="rounded-xl bg-slate-50 px-4 py-3 text-xs text-slate-500">Waiting for the taxi's live location…</p>;
  }
  const target = ride.status === "in_progress" ? ride.dropoff : ride.pickup;
  const km = route?.distance_km ?? kmBetween(loc, target);
  const mins = route?.duration_min ?? Math.max(1, Math.round((km / 25) * 60));
  return (
    <div data-testid="taxi-live-tracker" className="flex items-center gap-3 rounded-xl bg-blue-50 px-4 py-3">
      <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-500 opacity-75" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-blue-600" /></span>
      <p className="text-sm text-blue-900" data-testid="taxi-distance-text">
        {ride.status === "arrived" ? "Your taxi is at the pickup point" : `${km.toFixed(1)} km away · ~${mins} min ${ride.status === "in_progress" ? "to destination" : "to pickup"}`}
      </p>
      <span className="ml-auto text-[10px] text-blue-400">{fmtDate(loc.updated_at)}</span>
    </div>
  );
};

const DriverCard = ({ ride }) => (
  <div className="rounded-2xl border border-blue-100 bg-white p-5" data-testid="assigned-driver-card">
    <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Your driver</p>
    <div className="mt-3 flex items-center gap-4">
      <DriverAvatar id={ride.driver_id} v={ride.driver_photo_v} name={ride.driver_name} size="h-16 w-16" testId="assigned-driver-photo" />
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-slate-900" data-testid="assigned-driver-name">{ride.driver_name}</p>
        <p className="text-sm text-slate-500">
          {ride.vehicle ? `${ride.vehicle.color} ${ride.vehicle.make} ${ride.vehicle.model} · ${ride.vehicle.plate} · #${ride.vehicle.taxi_no}` : "Comfort Taxi"}
        </p>
      </div>
      {ride.driver_phone && (
        <a href={`tel:${ride.driver_phone}`} data-testid="call-driver-button" className="rounded-full bg-blue-50 p-3 text-blue-700 hover:bg-blue-100"><Phone className="h-4 w-4" /></a>
      )}
    </div>
  </div>
);

const EditPoint = ({ ride, field, label, onChange }) => {
  const [open, setOpen] = useState(false);
  const save = async (p) => {
    try {
      await api.put(`/bookings/${ride.id}/points`, { [field]: p });
      toast.success(`${label} updated — your driver has been notified`);
      setOpen(false);
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!open) {
    return (
      <button data-testid={`change-${field}-button`} onClick={() => setOpen(true)} className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:underline">
        <Pencil className="h-3 w-3" /> Change
      </button>
    );
  }
  return (
    <div className="mt-2">
      <PlaceSearch label={`New ${label.toLowerCase()}`} value={null} onChange={save} active={false} onActivate={() => {}} color={field === "pickup" ? "bg-blue-700" : "bg-amber-500"} testId={`edit-${field}`} allowMyLocation={field === "pickup"} />
      <button onClick={() => setOpen(false)} className="mt-1 text-xs text-slate-500 hover:underline">Cancel</button>
    </div>
  );
};

const FareBox = ({ ride, settings }) => {
  if (ride.status === "completed") {
    return <p data-testid="final-fare-basis" className="mt-2 text-xs text-slate-500">{ride.fare_basis === "gps" ? `Metered by GPS · ${ride.distance_km} km driven` : `Charged at estimate · ${ride.distance_km} km`}</p>;
  }
  if (ride.status === "in_progress" && settings) {
    const km = ride.tracked_km || 0;
    const fare = Math.max(settings.base_fare + settings.per_km * km, settings.min_fare);
    return (
      <p data-testid="gps-meter" className="mt-3 flex items-center gap-2 rounded-xl bg-blue-50 px-3 py-2 text-xs text-blue-900">
        <Gauge className="h-4 w-4 text-blue-700" /> GPS meter: <b className="font-mono-num">{km.toFixed(2)} km · {kina(fare)}</b>
        <span className="ml-auto text-blue-400">est. {kina(ride.estimated_fare ?? ride.fare)}</span>
      </p>
    );
  }
  return <p className="mt-2 text-xs text-slate-500">Estimate · {ride.estimated_km ?? ride.distance_km} km by road. Final fare metered by GPS.</p>;
};

const useRideRoute = (ride) => {
  const loc = ride.driver_location;
  const pts =
    ride.status === "accepted" && loc ? [loc, ride.pickup]
      : ride.status === "in_progress" ? [loc || ride.pickup, ride.dropoff]
        : ride.status === "completed" ? null : [ride.pickup, ride.dropoff];
  return useRoute(pts);
};

export const ActiveRide = ({ ride, onDone, onChange }) => {
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);
  const sharing = ["requested", "accepted", "arrived"].includes(ride.status);
  const me = useGps(sharing, `/bookings/${ride.id}/customer-location`, "Turn on location so your driver can find you");
  const route = useRideRoute(ride);
  const [track, setTrack] = useState(null);
  useEffect(() => {
    if (ride.status === "completed") api.get(`/bookings/${ride.id}/track`).then((r) => setTrack(r.data.points)).catch(() => {});
  }, [ride.id, ride.status]);
  const cancel = async () => {
    try {
      await api.post(`/bookings/${ride.id}/cancel`);
      toast.success("Ride cancelled");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
      onChange();
    }
  };
  const done = ride.status === "completed";
  return (
    <div className="grid gap-6 lg:grid-cols-12" data-testid="active-ride-panel">
      <div className="lg:col-span-7">
        <MapPicker pickup={ride.pickup} dropoff={ride.dropoff} taxi={ride.driver_location} passenger={sharing ? me.pos : null} route={route?.geometry} track={track} fitKey={ride.status} className="h-[340px] sm:h-[480px]" />
        {sharing && (
          <p data-testid="passenger-gps-status" className={`mt-2 flex items-center gap-2 text-xs ${me.error ? "text-red-600" : "text-emerald-600"}`}>
            <span className={`h-2 w-2 rounded-full ${me.error ? "bg-red-500" : me.pos ? "bg-emerald-500" : "bg-slate-300"}`} />
            {me.error || (me.pos ? "Sharing your location with the driver for a smooth pickup" : "Getting your location…")}
          </p>
        )}
      </div>
      <div className="space-y-4 lg:col-span-5">
        <div className="rounded-2xl border border-blue-100 bg-white p-6">
          <div className="flex items-center justify-between">
            <StatusBadge status={ride.status} testId="active-ride-status" />
            <span className="font-mono-num text-xl font-semibold text-slate-900" data-testid="active-ride-fare">{kina(ride.fare)}</span>
          </div>
          <div className="mt-4 text-sm text-slate-600"><b className="text-blue-700">A</b> {ride.pickup.address}{["requested", "accepted"].includes(ride.status) && <EditPoint ride={ride} field="pickup" label="Pickup" onChange={onChange} />}</div>
          <div className="mt-1 text-sm text-slate-600"><b className="text-amber-600">B</b> {ride.dropoff.address}{ride.status !== "completed" && <EditPoint ride={ride} field="dropoff" label="Drop-off" onChange={onChange} />}</div>
          <FareBox ride={ride} settings={settings} />
          {ride.status === "requested" && <p data-testid="searching-hint" className="mt-2 text-xs text-slate-400">We'll keep looking for a driver for up to 30 minutes.</p>}
          <div className="mt-6"><RideTimeline status={ride.status} /></div>
        </div>
        {ride.driver_name && <DriverCard ride={ride} />}
        {ride.driver_name && !done && <TaxiTracker ride={ride} route={route} />}
        {done ? (
          <div className="rounded-2xl bg-emerald-50 p-5 text-emerald-800">
            <p className="flex items-center gap-2 font-semibold"><CheckCircle2 className="h-5 w-5" /> You've arrived. Please pay {kina(ride.fare)} cash to your driver.</p>
            <div className="mt-4 rounded-xl bg-white p-4 text-slate-900" data-testid="rate-driver-panel">
              <p className="mb-2 text-sm font-semibold">How was your ride with {ride.driver_name}?</p>
              <RateRide ride={ride} onRated={onChange} />
            </div>
            <button data-testid="new-ride-button" onClick={onDone} className="mt-4 rounded-xl bg-blue-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-600">Book another ride</button>
          </div>
        ) : (
          ride.status !== "in_progress" && (
            <button data-testid="cancel-ride-button" onClick={cancel} className="h-11 w-full rounded-xl border border-red-200 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50">
              Cancel ride
            </button>
          )
        )}
      </div>
    </div>
  );
};
